import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { apiCall } from '@/utils/apiAxios';
import { AccountInfo } from '@azure/msal-browser';
import { useAgentStore } from '@/stores/agent';
import { useDashboardStore } from '@/stores/dashboard/main/dashboard_v1';
import { Profile } from '@/types/user/user';
import { showApiError } from '@/utils/vueSweetalert2';
import * as Sentry from '@sentry/vue';
import { getEnableFirstRoute } from '@/utils/getEnableFirstRoute';
import { usePermitsStore } from '@/stores/user/permissions/permissions';
import { useAlertStore } from '@/stores/alert';

export const useAuthStore = defineStore('auth', () => {
    const agentStore = useAgentStore();
    const dashboardStore = useDashboardStore();
    const router = useRouter();
    const route = useRoute();
    const alertStore = useAlertStore();
    // State
    const user = ref<Profile | null>(JSON.parse(localStorage.getItem('user') as string) || null);
    const sessionHash = ref(localStorage.getItem('session_hash') || '');
    const xApiKey = ref(localStorage.getItem('apikey') || null);
    const returnUrl = ref<string | null>(null);
    const returnName = ref<string | null>(null);
    const validEmail = ref(false);
    const loginValidateEmail = ref(false);
    const acceptanceValidation = ref(false);
    const apiAcceptance = ref(false);
    const isSubmitting = ref(false);
    const groups = ref(JSON.parse(localStorage.getItem('groups') as string) || []);
    const otp = ref('');

    const imgEmptyUserImage = ref('');

    // Varibles de impersonación
    const impersonate = ref(false);
    const impersonateUser = ref(null);
    // 1 = sin permisos, 2 = con permisos de ver todo
    const impersonateLevel = ref<number>(JSON.parse(sessionStorage.getItem('impersonateLevel') as string) || 2);
    const impersonateLoading = ref(true);
    const impersonateError = ref(false);

    const isLoadingLogin = ref(false);
    const isLoadingOtp = ref(false);
    const isSummitingOtp = ref(false);


    // Actions
    const login = async (username: string, password: string, acceptance: boolean, hash: string | string[]) => {
        isLoadingLogin.value = true;

        const data_post = {
            username,
            password,
            module: 'brokerin',
            acceptance
        };

        const { request } = await apiCall({
            method: 'post',
            url: `/auth/api/login`,
            data: data_post
        });

        try {
            const response = await request;

            if (response.status == 1) {
                user.value = response.info;
                xApiKey.value = response.api_key;
                groups.value = response.group;
                sessionHash.value = response.info.user_session_time_hash;

                impersonateLevel.value = 2;

                const firstnameInitial = user.value?.first_name.charAt(0) || 'N';
                const lastnameInitial = user.value?.last_name.charAt(0) || 'A';

                imgEmptyUserImage.value = `https://dummyimage.com/128x128/b8b8b8/ffffff.jpg&text=+${firstnameInitial}+${lastnameInitial}`;

                localStorage.setItem('user', JSON.stringify(response.info));
                localStorage.setItem('session_hash', response.info.user_session_time_hash);
                localStorage.setItem('groups', JSON.stringify(response.group));
                localStorage.setItem('apikey', response.api_key);
                localStorage.setItem('date-login', new Date().toISOString());
                sessionStorage.setItem('impersonateLevel', JSON.stringify(impersonateLevel.value));

                // Configurar usuario en Sentry
                if (import.meta.env.PROD) {
                    Sentry.setUser({
                        id: response.info.id?.toString(),
                        email: response.info.email,
                        username: `${response.info.first_name} ${response.info.last_name}`
                    });
                }

                document.cookie = `X-API-KEY=${response.api_key}; SameSite=None; Secure; path=/; max-age=86400`; // Expira en 1 día
                // SameSite=None es para que el cookie no afecte a la navegación por que el dominio son diferentes
                
                // Cargar permisos antes de redirigir para que getEnableFirstRoute() funcione correctamente
                const storePermits = usePermitsStore();
                await storePermits.getPermits();
                
                if (hash == '' || hash === undefined) {
                    if (returnName.value == '' || returnName.value === undefined || returnName.value === null) {
                        router.push({
                            name: getEnableFirstRoute()
                        });
                    } else {
                        router.push({
                            name: returnName.value
                        });
                    }
                } else {
                    router.push({
                        name: 'Detalle de la Actividad',
                        params: {
                            hash_folio: route.params.hash_folio
                        }
                    });
                }

                
                setTimeout(() => {
                    validEmail.value = false;
                    apiAcceptance.value = false;
                    acceptanceValidation.value = false;
                    isLoadingLogin.value = false;
                }, 2000);
                
                return true;
            } else {
                setTimeout(() => {
                    isLoadingLogin.value = false;
                }, 1000);

                return response;
            }
        } catch (error) {
            const errorMessage = 'Error del servidor. Por favor, intenta nuevamente.';
            setTimeout(() => {
                isLoadingLogin.value = false;
            }, 1000);

            return errorMessage;
        }
    };

    const impersonateLogin = async () => {
        impersonateLoading.value = true;

        const data_post = {
            login_hash: route.params.hash
        };

        const { request } = await apiCall({
            method: 'post',
            url: `/brokerin/api/users/impersonate_login`,
            data: data_post
        });

        const response = await request;

        impersonateLoading.value = false;

        if (response.status == 1) {
            user.value = response.response.info;
            xApiKey.value = response.response.api_key;
            groups.value = response.response.groups;
            sessionHash.value = response.response.info.user_session_time_hash;

            impersonateLevel.value = route.params.restricted == 'XLD' ? 2 : 1;

            const firstnameInitial = user.value?.first_name.charAt(0) || 'N';
            const lastnameInitial = user.value?.last_name.charAt(0) || 'A';

            imgEmptyUserImage.value = `https://dummyimage.com/128x128/b8b8b8/ffffff.jpg&text=+${firstnameInitial}+${lastnameInitial}`;

            localStorage.setItem('user', JSON.stringify(response.response.info));
            localStorage.setItem('session_hash', response.response.info.user_session_time_hash);
            localStorage.setItem('groups', JSON.stringify(response.response.groups));
            localStorage.setItem('apikey', response.response.api_key);

            // Configurar usuario en Sentry (impersonate)
            if (import.meta.env.PROD) {
                Sentry.setUser({
                    id: response.response.info.id?.toString(),
                    email: response.response.info.email,
                    username: `${response.response.info.first_name} ${response.response.info.last_name} (Impersonate)`
                });
            }
            localStorage.setItem('date-login', new Date().toISOString());
            sessionStorage.setItem('impersonateLevel', JSON.stringify(impersonateLevel.value));

            document.cookie = `X-API-KEY=${response.response.api_key}; SameSite=None; Secure; path=/; max-age=86400`; // Expira en 1 día
            // SameSite=None es para que el cookie no afecte a la navegación por que el dominio son diferentes

            router.push({
                name: 'Mi Tablero'
            });

            validEmail.value = false;
            apiAcceptance.value = false;
            acceptanceValidation.value = false;
        } else {
            impersonateError.value = true;
            return response.message;
        }
    };

    const loginMicrosoft = async (username: string, token: string) => {
        const data_post = {
            username,
            token,
            provider: 4
        };

        const { request } = await apiCall({
            method: 'post',
            url: `/auth/api/login/login_social`,
            data: data_post
        });

        const response = await request;

        if (response.status == 1) {
            user.value = response.info;
            xApiKey.value = response.api_key;
            groups.value = response.group;

            localStorage.setItem('user', JSON.stringify(response.info));
            localStorage.setItem('groups', JSON.stringify(response.group));
            localStorage.setItem('apikey', response.api_key);

            // Configurar usuario en Sentry
            if (import.meta.env.PROD) {
                Sentry.setUser({
                    id: response.info.id?.toString(),
                    email: response.info.email,
                    username: `${response.info.first_name} ${response.info.last_name}`
                });
            }
            localStorage.setItem('date-login', new Date().toISOString());

            router.push({
                name: 'Mi Tablero'
            });

            validEmail.value = false;
            apiAcceptance.value = false;
            acceptanceValidation.value = false;
        } else {
            console.error(response.message);
        }
    };

    const validateEmail = async (username: string) => {
        loginValidateEmail.value = true;
        isSubmitting.value = true;        

        try {
            const data = {
                email: username,
                module: 'brokerin'
            };

            const { request } = await apiCall({
                method: 'get',
                url: `/auth/api/login/validate_mail`,
                data: {},
                params: data
            });

            const response = await request;

            if (response.status == 1) {
                validEmail.value = true;

                apiAcceptance.value = response.response.acceptance;
                acceptanceValidation.value = response.response.acceptance;

                loginValidateEmail.value = false;
                isSubmitting.value = false;
                return true;
            } else {
                loginValidateEmail.value = false;
                isSubmitting.value = false;
                return response.response;
            }
        } catch (error) {
            console.error(error + ' (validateEmail)');
            return error;
        }
    };

    const logout = async (redirect = true) => {
        if (redirect) {
            const agentHash = sessionStorage.getItem('agentHash');
            const actionId = 14;
            try {
                const { request } = apiCall({
                    method: 'post',
                    url: '/brokerin/api/users_session/register_user_action',
                    data: {
                        session_hash: sessionHash.value,
                        action_id: actionId,
                        agent_hash: agentHash
                    }
                });
                await request;
            } catch (error) {
                console.error('Error al registrar la acción de logout:', error);
            }
        }

        isSubmitting.value = false;
        user.value = null;
        xApiKey.value = null;
        impersonate.value = false;
        impersonateLevel.value = 0;
        
        // Limpiar usuario de Sentry
        if (import.meta.env.PROD) {
            Sentry.setUser(null);
        }
        
        localStorage.removeItem('user');
        localStorage.removeItem('apikey');
        localStorage.removeItem('timeToLogout');
        localStorage.clear();
        sessionStorage.clear();
        document.cookie = 'X-API-KEY=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        agentStore.resetAllVariables();
        dashboardStore.resetAllVariables();
        if (redirect) {
            window.location.href = '/';
        }
        return;
    };

    const account = ref<AccountInfo | null>(null);
    const token = ref<string | null>(null);

    const setAccount = (accountInfo: AccountInfo) => {
        account.value = accountInfo;
    };

    const setToken = (accessToken: string) => {
        token.value = accessToken;
    };

    function getUserGroupByName(groupName: string) {
        return groups.value.find((group: { name: string }) => group.name == groupName);
    }

    const validatePassword = async (password: string, password_confirm: string, token: string) => {
        try {
            const url = `/brokerin/api/auth/br_password_recovery`;
            const data = {
                password: password,
                password_confirm: password_confirm,
                token: token
            };

            const { request } = await apiCall({
                method: 'post',
                url: url,
                data
            });

            const response = await request;

            return response.status;

            // if (response.status == 1) {
            //     alertStore.addAlert({
            //         message: `La nueva contraseña se guardó con éxito.`,
            //         type: 'success',
            //         duration: 4000
            //     });
            //     return response.status;
            // } else {
            //     showApiError(url, response, null);
            // }
        } catch (error) {
            console.error(error + ' (validateEmail)');
            return error;
        }
    };

    const validateToken = async (token: string | string[]) => {
        try {
            const url = `/brokerin/api/auth/br_verify_token`;
            const data = {
                token: token
            };

            const { request } = await apiCall({
                method: 'post',
                url: url,
                data
            });

            const response = await request;
            if (response.status == 0) {
                showApiError(url, response, null);
            }

            return response.response;
        } catch (error) {
            console.error(error + ' (br_verify_token)');
            return error;
        }
    };

    const generateOtpAuth = async (username: string, password: string, phoneNumber: string) => {
        const url = `/auth/api/login/request_otp`;
        const data = {
            username: username,
            password: password,
            phone: phoneNumber
        };

        const { request } = await apiCall({
            method: 'post',
            url: url,
            data: data
        });

        try {
            isLoadingOtp.value = true;
            const response = await request;
            if (response.status == 1) {
                isLoadingOtp.value = false;  
                otp.value = '';         
                return true;
            } else {
                otp.value = '';
                isLoadingOtp.value = false; 
                alertStore.addAlert({
                    message: response.message,
                    type: 'error',
                    duration: 5000
                });
                return false;
            }   
        } catch (error) {
            isLoadingOtp.value = false;        
            return false;
        }
    };

    const verifyOtpAuth = async (username: string, password: string, phoneNumber: string, hash: string | string[]) => {

        if(otp.value.length !== 6) {
            return false;
        }


        const url = `/auth/api/login/verify_otp`;
        const data = {
            username: username,
            password: password,
            otp_code: otp.value,
            phone: phoneNumber,
            module: 'brokerin'
        };

        const { request } = await apiCall({
            method: 'post',
            url: url,
            data: data
        });

        isSummitingOtp.value = true;
        const response = await request;

        if (response.status == 1) {
            user.value = response.info;
            xApiKey.value = response.api_key;
            groups.value = response.group;
            sessionHash.value = response.info.user_session_time_hash;

            impersonateLevel.value = 2;

            const firstnameInitial = user.value?.first_name.charAt(0) || 'N';
            const lastnameInitial = user.value?.last_name.charAt(0) || 'A';

            imgEmptyUserImage.value = `https://dummyimage.com/128x128/b8b8b8/ffffff.jpg&text=+${firstnameInitial}+${lastnameInitial}`;

            localStorage.setItem('user', JSON.stringify(response.info));
            localStorage.setItem('session_hash', response.info.user_session_time_hash);
            localStorage.setItem('groups', JSON.stringify(response.group));
            localStorage.setItem('apikey', response.api_key);
            localStorage.setItem('date-login', new Date().toISOString());
            sessionStorage.setItem('impersonateLevel', JSON.stringify(impersonateLevel.value));

            // Configurar usuario en Sentry
            if (import.meta.env.PROD) {
                Sentry.setUser({
                    id: response.info.id?.toString(),
                    email: response.info.email,
                    username: `${response.info.first_name} ${response.info.last_name}`
                });
            }

            document.cookie = `X-API-KEY=${response.api_key}; SameSite=None; Secure; path=/; max-age=86400`; // Expira en 1 día
            // SameSite=None es para que el cookie no afecte a la navegación por que el dominio son diferentes
            
            // Cargar permisos antes de redirigir para que getEnableFirstRoute() funcione correctamente
            const storePermits = usePermitsStore();
            await storePermits.getPermits();
            
            if (hash == '' || hash === undefined) {
                isSummitingOtp.value = false;
                if (returnName.value == '' || returnName.value === undefined || returnName.value === null) {
                    router.push({
                        name: getEnableFirstRoute()
                    });
                } else {
                    router.push({
                        name: returnName.value
                    });
                }
            } else {
                isSummitingOtp.value = false;
                router.push({
                    name: 'Detalle de la Actividad',
                    params: {
                        hash_folio: route.params.hash_folio
                    }
                });
            }

            
            setTimeout(() => {
                validEmail.value = false;
                apiAcceptance.value = false;
                acceptanceValidation.value = false;
                isLoadingLogin.value = false;
            }, 2000);
        } else {
            alertStore.addAlert({
                message: response.message,
                type: 'error',
                duration: 5000
            });
            otp.value = ''
            isSummitingOtp.value = false;
            return false;
        }
    };

    return {
        user,
        groups,
        xApiKey,
        sessionHash,
        returnUrl,
        returnName,
        validEmail,
        loginValidateEmail,
        acceptanceValidation,
        apiAcceptance,
        account,
        token,
        isSubmitting,
        isLoadingLogin,
        isLoadingOtp,
        isSummitingOtp,
        otp,
        impersonate,
        impersonateUser,
        impersonateLevel,
        impersonateLoading,
        impersonateError,
        imgEmptyUserImage,
        setAccount,
        setToken,
        login,
        impersonateLogin,
        loginMicrosoft,
        validateEmail,
        getUserGroupByName,
        logout,
        validatePassword,
        validateToken,
        generateOtpAuth,
        verifyOtpAuth
    };
});
